/**
 * useActiveTenant - Standardized hook for tenant context with impersonation support
 * 
 * TENANT ISOLATION: This hook provides the correct tenant ID that respects:
 * 1. Sysadmin impersonation (impersonated_tenant_id in localStorage)
 * 2. User's actual tenant from auth context
 * 
 * Usage:
 *   const { tenantId, isImpersonating } = useActiveTenant();
 *   // tenantId is always the correct tenant to use for API calls
 */

import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';

export const useActiveTenant = () => {
  const { user } = useAuth();
  const { 
    activeTenantId, 
    activeTenantName, 
    isImpersonating,
    tenant 
  } = useTenant();

  // Memoize the computed values to prevent unnecessary re-renders
  const result = useMemo(() => {
    // Priority order:
    // 1. activeTenantId from TenantContext (already handles impersonation)
    // 2. Fallback to localStorage directly (for edge cases)
    // 3. User's tenant from auth context
    const impersonatedId = localStorage.getItem('impersonated_tenant_id');
    const userTenantId = user?.tenant?.id || user?.tenant_id;
    
    const effectiveTenantId = activeTenantId || impersonatedId || userTenantId;
    const effectiveTenantName = activeTenantName || 
      localStorage.getItem('impersonated_tenant_name') || 
      user?.tenant?.name || 
      'Unknown Tenant';

    return {
      // The tenant ID to use for all API calls
      tenantId: effectiveTenantId,
      
      // The tenant name for display purposes
      tenantName: effectiveTenantName,
      
      // Whether a sysadmin is currently impersonating a tenant
      isImpersonating: isImpersonating || !!impersonatedId,
      
      // The user's actual tenant ID (not impersonated)
      userTenantId: userTenantId,
      
      // The full tenant object from context (may be user's tenant, not impersonated)
      tenant: tenant,
      
      // Helper to check if tenant context is ready
      hasTenant: !!effectiveTenantId,
    };
  }, [activeTenantId, activeTenantName, isImpersonating, tenant, user]);

  return result;
};

export default useActiveTenant;
